import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Star } from "lucide-react";
import { GOOGLE_REVIEW_URL } from "@/content/reviewLinks";
import { gtagReportConversion } from "@/lib/gtag";
import { clearPendingBooking } from "@/lib/booking5/pendingBooking";

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking_id");

  useEffect(() => {
    clearPendingBooking();
  }, []);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const id = params.get("booking_id");
      if (!id) return;

      const key = `purchase_fired_${id}`;
      if (sessionStorage.getItem(key)) return;

      const rawTotal = params.get("total") || "";
      const total = parseFloat(rawTotal.replace(/[^0-9.]/g, "")) || 0;

      const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ ecommerce: null });
      w.dataLayer.push({
        event: "purchase",
        ecommerce: {
          transaction_id: id,
          value: total,
          currency: "USD",
        },
      });

      gtagReportConversion();
      sessionStorage.setItem(key, "1");
    } catch {
      // analytics must never break the thank-you page
    }
  }, []);


  useEffect(() => {
    const container = document.getElementById("waiver-container");
    if (!container) return;

    if (bookingId) {
      const iframe = document.createElement("iframe");
      iframe.src = `https://sonsguadalupe.checkfront.com/reserve/document/?template_id=1&id=${bookingId}`;
      iframe.width = "100%";
      iframe.height = "900px";
      iframe.style.border = "none";
      iframe.style.marginTop = "20px";
      container.appendChild(iframe);
    } else {
      container.innerText = "Booking ID not found. Unable to display waiver.";
    }

    return () => {
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [bookingId]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title="Thank You for Your Booking | Son's Guadalupe"
        description="Your booking is confirmed! Complete your waiver and forward it to all adult guests before your visit."
        noIndex
      />
      <Header />
      <main className="flex-1">
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Thank you!
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              We just emailed your booking receipt and rental agreement along with a shareable waiver link. You may complete your waiver below.
            </p>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div id="waiver-container" className="flex justify-center items-center min-h-[200px]"></div>
            
            <div className="mt-8 text-center max-w-2xl mx-auto space-y-4">
              <p className="text-primary font-bold">
                Please forward the Waiver to all adult guests if you have not done so already.
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Please Note:</strong> Everyone in your group (adults must also sign for minors) must sign a waiver before entering the property. You can forward the rental agreement/waiver from the email we just sent you.
              </p>
              <p className="text-muted-foreground">
                Need Help? Call us at: <a href="tel:877-577-7667" className="text-primary hover:underline font-medium">877-577-7667</a>
              </p>
            </div>

            <div className="mt-12 max-w-2xl mx-auto">
              <div className="bg-teal/5 border border-teal/20 rounded-2xl p-6 md:p-8 text-center">
                <div className="flex justify-center gap-1 mb-3">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                  Loved your stay with us?
                </h2>
                <p className="text-muted-foreground mb-5">
                  Your review on Google helps other families find their perfect riverside getaway. It takes less than a minute.
                </p>
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { (window as any).dataLayer = (window as any).dataLayer || []; (window as any).dataLayer.push({ event: "review_click" }); }}
                  className="inline-flex items-center gap-2 bg-teal text-white px-6 py-3 rounded-full font-medium hover:bg-teal/90 transition-colors"
                >
                  <Star className="w-4 h-4 fill-white" />
                  Leave us a Google review
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ThankYou;
